import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowsLeftRight, EnvelopeSimple, PaperPlaneTilt, StopCircle,
  Trash, CheckCircle, XCircle, Warning, ArrowClockwise, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { absTime } from "../lib/labels";

const emptyEndpoint = { host: "", port: 993, email: "", password: "", use_ssl: true };

function EndpointCard({ title, ep, setEp, onTest, tested, testing }) {
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold flex items-center gap-2"><EnvelopeSimple size={16}/> {title}</div>
        {tested === true && <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill"/> connecté</span>}
        {tested === false && <span className="text-xs text-red-700 inline-flex items-center gap-1"><XCircle size={13} weight="fill"/> échec</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2"><Label>Serveur</Label><Input value={ep.host} onChange={(e)=>setEp({...ep, host:e.target.value})} placeholder="imap.exemple.fr" data-testid={`im-${title}-host`}/></div>
        <div><Label>Port</Label><Input type="number" value={ep.port} onChange={(e)=>setEp({...ep, port:parseInt(e.target.value)||993})}/></div>
      </div>
      <div><Label>Email</Label><Input type="email" value={ep.email} onChange={(e)=>setEp({...ep, email:e.target.value})} placeholder="alice@exemple.fr" data-testid={`im-${title}-email`}/></div>
      <div><Label>Mot de passe</Label><Input type="password" value={ep.password} onChange={(e)=>setEp({...ep, password:e.target.value})} data-testid={`im-${title}-password`}/></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ep.use_ssl} onChange={(e)=>setEp({...ep, use_ssl:e.target.checked, port: e.target.checked ? 993 : 143})}/> IMAP over SSL/TLS (recommandé)</label>
      <Button variant="outline" size="sm" onClick={onTest} disabled={testing || !ep.host || !ep.email || !ep.password} data-testid={`im-${title}-test`}>
        {testing ? "Test…" : "Tester la connexion"}
      </Button>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    queued:    ["bg-slate-200 text-slate-800", "En file"],
    running:   ["bg-blue-100 text-blue-800", "En cours"],
    done:      ["bg-emerald-100 text-emerald-800", "Terminé"],
    failed:    ["bg-red-100 text-red-800", "Échec"],
    cancelled: ["bg-amber-100 text-amber-800", "Annulé"],
  };
  const [cls, label] = map[status] || ["bg-muted", status];
  return <span className={`text-[0.65rem] px-1.5 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>;
}

function ResumeDialog({ job, onClose, onResumed }) {
  const [srcPwd, setSrcPwd] = useState("");
  const [dstPwd, setDstPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!srcPwd || !dstPwd) { toast.error("Merci de saisir les deux mots de passe"); return; }
    setSubmitting(true);
    try {
      await api.post(`/imap-migration/jobs/${job.id}/resume`, {
        source_password: srcPwd,
        destination_password: dstPwd,
      });
      toast.success(`Reprise démarrée · reprise à partir du ${job.folders_completed?.length || 0}ᵉ dossier terminé`);
      onResumed();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!job} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent data-testid="im-resume-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowsCounterClockwise size={18}/> Reprendre la migration</DialogTitle>
          <DialogDescription>
            Les mots de passe ont été effacés par sécurité à la fin du job précédent — merci de les saisir à nouveau.
            La reprise se fera après <b>{job?.folders_completed?.length || 0}</b> dossier(s) déjà terminé(s) sur {job?.folders_total || 0}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Mot de passe source <span className="text-xs text-muted-foreground">({job?.source?.email})</span></Label>
            <Input type="password" value={srcPwd} onChange={(e)=>setSrcPwd(e.target.value)} data-testid="im-resume-src-pwd" autoFocus/>
          </div>
          <div>
            <Label>Mot de passe destination <span className="text-xs text-muted-foreground">({job?.destination?.email})</span></Label>
            <Input type="password" value={dstPwd} onChange={(e)=>setDstPwd(e.target.value)} data-testid="im-resume-dst-pwd"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} data-testid="im-resume-submit">
            <ArrowsCounterClockwise size={14} className="mr-1"/> {submitting ? "Reprise…" : "Reprendre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ImapMigration() {
  const nav = useNavigate();
  const [src, setSrc] = useState(emptyEndpoint);
  const [dst, setDst] = useState(emptyEndpoint);
  const [srcTested, setSrcTested] = useState(null);
  const [dstTested, setDstTested] = useState(null);
  const [srcFolders, setSrcFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [testing, setTesting] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [starting, setStarting] = useState(false);
  const [resumeJob, setResumeJob] = useState(null);
  // Optional wrapper folder on the destination — keeps the imported mailbox
  // isolated from the existing folders on the destination account.
  const [wrapEnabled, setWrapEnabled] = useState(false);
  const [wrapName, setWrapName] = useState("Migration-ancien");

  const load = () => api.get("/imap-migration/jobs").then((r)=>setJobs(r.data.items||[])).catch(()=>{});
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);

  const test = async (which, ep, setTested) => {
    setTesting(which);
    try {
      const r = await api.post("/imap-migration/test-connection", ep);
      setTested(true);
      if (which === "source") { setSrcFolders(r.data.folders || []); setSelectedFolders(r.data.folders || []); }
      toast.success(`Connexion ${which} OK — ${r.data.folders?.length || 0} dossier(s)`);
    } catch (e) {
      setTested(false);
      if (which === "source") { setSrcFolders([]); setSelectedFolders([]); }
      toast.error(e?.response?.data?.detail || "Connexion échouée");
    } finally { setTesting(null); }
  };

  const start = async () => {
    if (srcTested !== true || dstTested !== true) { toast.error("Testez d'abord les 2 connexions"); return; }
    if (src.email.toLowerCase() === dst.email.toLowerCase() && src.host.toLowerCase() === dst.host.toLowerCase()) {
      toast.error("Source et destination identiques"); return;
    }
    if (!window.confirm(`Copier ${selectedFolders.length || "tous les"} dossiers de ${src.email} → ${dst.email} ?`)) return;
    const prefix = wrapEnabled ? (wrapName || "").trim() : "";
    if (wrapEnabled && !prefix) { toast.error("Merci de saisir un nom de dossier destination"); return; }
    setStarting(true);
    try {
      await api.post("/imap-migration/start", {
        source: src, destination: dst,
        folders: selectedFolders.length === srcFolders.length ? null : selectedFolders,
        dest_prefix: prefix || null,
      });
      toast.success(prefix
        ? `Migration démarrée · tout ira sous "${prefix}/…"`
        : "Migration démarrée en arrière-plan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setStarting(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/imap-migration/jobs/${id}/cancel`); toast.success("Annulation demandée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce job de l'historique ?")) return;
    try { await api.delete(`/imap-migration/jobs/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleFolder = (f) => {
    setSelectedFolders(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };
  const allChecked = useMemo(() => srcFolders.length > 0 && selectedFolders.length === srcFolders.length, [srcFolders, selectedFolders]);

  return (
    <div className="p-6 max-w-5xl">
      <button onClick={() => nav("/modules")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="im-back">
        <ArrowLeft size={16}/> Modules
      </button>
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2"><ArrowsLeftRight size={26}/> Migration IMAP</h1>
        <p className="text-sm text-muted-foreground">Copie d'une boîte email IMAP vers une autre — dossiers, messages, drapeaux (lu/non lu) et dates d'origine préservés. Idéal pour un changement d'hébergeur. Un rapport de synthèse est envoyé par email en fin de migration.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <EndpointCard title="source" ep={src} setEp={setSrc} onTest={() => test("source", src, setSrcTested)} tested={srcTested} testing={testing === "source"}/>
        <EndpointCard title="destination" ep={dst} setEp={setDst} onTest={() => test("destination", dst, setDstTested)} tested={dstTested} testing={testing === "destination"}/>
      </div>

      {srcFolders.length > 0 && (
        <div className="rounded-xl border bg-card p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Dossiers à migrer ({selectedFolders.length}/{srcFolders.length})</div>
            <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={allChecked}
                     onChange={() => setSelectedFolders(allChecked ? [] : [...srcFolders])}
                     data-testid="im-folders-all"/> Tout (dé)sélectionner
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 text-xs max-h-64 overflow-auto">
            {srcFolders.map(f => (
              <label key={f} className="flex items-center gap-1.5 py-0.5 cursor-pointer hover:bg-muted/40 rounded px-1" data-testid={`im-folder-${f}`}>
                <input type="checkbox" checked={selectedFolders.includes(f)} onChange={() => toggleFolder(f)}/>
                <span className="mono truncate">{f}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card p-4 mb-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={wrapEnabled}
                 onChange={(e) => setWrapEnabled(e.target.checked)}
                 data-testid="im-wrap-enabled"/>
          <div className="flex-1">
            <div className="text-sm font-medium">Ranger tout dans un dossier spécifique côté destination</div>
            <div className="text-xs text-muted-foreground mb-2">
              Utile pour ne pas mélanger avec la boîte existante — l'arborescence source sera recréée SOUS ce dossier
              (ex. <span className="mono">INBOX</span> → <span className="mono">{wrapName || "Migration-ancien"}/INBOX</span>).
            </div>
            {wrapEnabled && (
              <Input value={wrapName} onChange={(e) => setWrapName(e.target.value)}
                     placeholder="Nom du dossier — ex. Migration-ancien"
                     className="max-w-sm" data-testid="im-wrap-name"/>
            )}
          </div>
        </label>
      </div>

      <div className="flex justify-end mb-6">
        <Button onClick={start} disabled={starting || srcTested !== true || dstTested !== true} data-testid="im-start">
          <PaperPlaneTilt size={16} className="mr-1"/>{starting ? "Démarrage…" : "Démarrer la migration"}
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Historique des migrations</div>
          <Button variant="ghost" size="sm" onClick={load}><ArrowClockwise size={13}/></Button>
        </div>
        {jobs.length === 0 && <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm text-muted-foreground">Aucune migration lancée.</div>}
        <div className="space-y-2">
          {jobs.map(j => {
            const pct = j.messages_total > 0 ? Math.round((j.messages_done / j.messages_total) * 100) : 0;
            const resumable = (j.status === "failed" || j.status === "cancelled");
            const completedCount = j.folders_completed?.length || 0;
            return (
              <div key={j.id} className="rounded-xl border bg-card p-3" data-testid={`im-job-${j.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{j.source?.email} → {j.destination?.email} <StatusPill status={j.status}/></div>
                    <div className="text-xs text-muted-foreground">
                      {j.folders_done}/{j.folders_total} dossiers · {j.messages_done}/{j.messages_total} messages
                      {j.dest_prefix && <> · <span className="text-blue-700">rangés sous <span className="mono">{j.dest_prefix}/</span></span></>}
                      {completedCount > 0 && resumable && <> · <span className="text-emerald-700">{completedCount} dossier(s) terminé(s) conservé(s)</span></>}
                      {j.current_folder && j.status === "running" && <> · <span className="mono">{j.current_folder}</span></>}
                      {j.errors?.length > 0 && <> · <span className="text-amber-700 inline-flex items-center gap-1"><Warning size={11}/> {j.errors.length} erreur(s)</span></>}
                    </div>
                    <div className="text-[0.65rem] text-muted-foreground">Lancée {absTime(j.created_at)} par {j.author_name}{j.finished_at ? ` · Terminée ${absTime(j.finished_at)}` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {(j.status === "queued" || j.status === "running") && (
                      <Button variant="ghost" size="sm" onClick={() => cancel(j.id)} data-testid={`im-cancel-${j.id}`}><StopCircle size={14} className="text-amber-600"/></Button>
                    )}
                    {resumable && (
                      <Button variant="outline" size="sm" onClick={() => setResumeJob(j)} data-testid={`im-resume-${j.id}`}>
                        <ArrowsCounterClockwise size={13} className="mr-1"/> Reprendre
                      </Button>
                    )}
                    {(j.status === "done" || j.status === "failed" || j.status === "cancelled") && (
                      <Button variant="ghost" size="sm" onClick={() => remove(j.id)}><Trash size={14} className="text-red-600"/></Button>
                    )}
                  </div>
                </div>
                {j.messages_total > 0 && (
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={"h-full transition-all " + (j.status === "failed" ? "bg-red-500" : j.status === "cancelled" ? "bg-amber-500" : j.status === "done" ? "bg-emerald-500" : "bg-primary")} style={{ width: `${pct}%` }}/>
                  </div>
                )}
                {j.error && <div className="text-xs text-red-700 mt-1">{j.error}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {resumeJob && <ResumeDialog job={resumeJob} onClose={() => setResumeJob(null)} onResumed={load}/>}
    </div>
  );
}
